import * as XLSX from "xlsx";
import type { DocumentLayout, TableBlock } from "./pdfLayout";
import { convertTable, mergeNumberFormats, numberFormatKind, parseDate, parseNumber, type DateOrder, type SheetCell } from "./cellValue";

/** One table found on the page(s) of the PDF, as plain text cells. */
export interface ExtractedTable {
  /** Pages (1-based) the table covers; more than one when a table continues onto the next page. */
  pages: number[];
  rows: string[][];
  /** Per row: every filled cell was bold in the PDF (a header hint). */
  boldRow: boolean[];
  /** Column boundaries in points, used to tell whether a table on the next page continues this one. */
  colExtents: { left: number; right: number }[];
  /** Rows (from the top) that are headings, not data. */
  headerRows: number;
  /** True when the text came from OCR, so values may need checking. */
  fromOcr?: boolean;
}

export type SheetMode = "table" | "page" | "single";

export interface ExcelOptions {
  sheetMode: SheetMode;
  /** Join a table that carries on over the page break into one table. */
  joinContinued: boolean;
  /** Write numbers, percentages, currency and dates as real values. */
  convertValues: boolean;
  dateOrder: DateOrder;
}

export const DEFAULT_EXCEL_OPTIONS: ExcelOptions = { sheetMode: "table", joinContinued: true, convertValues: true, dateOrder: "auto" };

const cellText = (cell: { runs: { text: string }[] }) => cell.runs.map((r) => r.text).join("").replace(/[\t\r\n]+/g, " ").replace(/\s{2,}/g, " ").trim();

function tableFromBlock(block: TableBlock, page: number, fromOcr?: boolean): ExtractedTable {
  const rows = block.rows.map((r) => r.map(cellText));
  const boldRow = block.rows.map((r) => {
    const filled = r.filter((c) => cellText(c));
    return filled.length > 0 && filled.every((c) => c.runs.filter((x) => x.text.trim()).every((x) => x.bold));
  });
  const colExtents = block.colExtents ?? block.colWidthsPt.map((_, i) => ({ left: block.leftPt + i, right: block.leftPt + i }));
  const table: ExtractedTable = { pages: [page], rows, boldRow, colExtents, headerRows: 0, fromOcr };
  table.headerRows = detectHeaderRows(table);
  return table;
}

/** Tables from a laid-out document; prose, headings and form text are not included. */
export function tablesFromLayout(layout: DocumentLayout, ocrPages: Set<number> = new Set()): ExtractedTable[] {
  const out: ExtractedTable[] = [];
  layout.pages.forEach((page, i) => {
    for (const block of page.blocks) {
      if (block.kind !== "table") continue;
      const t = tableFromBlock(block, i + 1, ocrPages.has(i + 1));
      // A table of one real column, or with no text at all, is not worth a sheet.
      if (t.rows.length >= 2 && t.rows.some((r) => r.filter(Boolean).length >= 2)) out.push(t);
    }
  });
  return out;
}

const isValueLike = (s: string) => !!(s && (parseNumber(s) || parseDate(s)));

/** 1 when the first row reads as a heading (no values in it while the rows below hold values, or it is all bold). */
export function detectHeaderRows(t: Pick<ExtractedTable, "rows" | "boldRow">): number {
  if (t.rows.length < 2) return 0;
  const first = t.rows[0];
  const filled = first.filter(Boolean);
  if (filled.length === 0) return 0;
  if (filled.some(isValueLike)) return 0;
  const below = t.rows.slice(1);
  const cols = Math.max(...t.rows.map((r) => r.length));
  let valueColumns = 0;
  for (let c = 0; c < cols; c++) {
    const col = below.map((r) => r[c] ?? "").filter(Boolean);
    if (col.length && col.filter(isValueLike).length / col.length >= 0.6 && first[c]) valueColumns++;
  }
  if (valueColumns > 0) return 1;
  const secondBold = t.boldRow[1];
  return t.boldRow[0] && !secondBold ? 1 : 0;
}

const norm = (r: string[]) => r.map((c) => c.toLowerCase().replace(/\s+/g, " ").trim()).join("|");

/** Whether `next` (top of the following page) carries on `prev` (end of the page before). */
export function continues(prev: ExtractedTable, next: ExtractedTable): boolean {
  const lastPage = prev.pages[prev.pages.length - 1];
  if (next.pages[0] !== lastPage + 1) return false;
  if (prev.colExtents.length !== next.colExtents.length) return false;
  // Every column must sit in the same place: the two pages' text for that column overlaps.
  return prev.colExtents.every((a, i) => {
    const b = next.colExtents[i];
    return Math.min(a.right, b.right) - Math.max(a.left, b.left) > -2;
  });
}

/** Joins tables that continue across page breaks, dropping a repeated heading row. Returns the new list. */
export function joinContinuedTables(tables: ExtractedTable[]): ExtractedTable[] {
  const out: ExtractedTable[] = [];
  for (const t of tables) {
    const prev = out[out.length - 1];
    if (prev && continues(prev, t)) {
      let rows = t.rows, bold = t.boldRow;
      const head = prev.headerRows > 0 ? norm(prev.rows[0]) : null;
      // The heading repeated at the top of the continuation page.
      if (head !== null && rows.length && norm(rows[0]) === head) {
        rows = rows.slice(1);
        bold = bold.slice(1);
      } else if (head === null && t.headerRows === 0 && prev.rows.length && rows.length && norm(rows[0]) === norm(prev.rows[0])) {
        rows = rows.slice(1);
        bold = bold.slice(1);
      }
      prev.rows = [...prev.rows, ...rows];
      prev.boldRow = [...prev.boldRow, ...bold];
      prev.pages = [...prev.pages, ...t.pages];
      prev.fromOcr = prev.fromOcr || t.fromOcr;
      continue;
    }
    out.push({ ...t, rows: [...t.rows], boldRow: [...t.boldRow], pages: [...t.pages] });
  }
  return out;
}

export interface SheetPlan {
  name: string;
  tables: ExtractedTable[];
}

const pageRangeLabel = (pages: number[]) => (pages.length === 1 ? `p${pages[0]}` : `p${pages[0]}-${pages[pages.length - 1]}`);

/** Groups tables into sheets according to the chosen mode. */
export function planSheets(tables: ExtractedTable[], mode: SheetMode): SheetPlan[] {
  if (mode === "single") return tables.length ? [{ name: "Tables", tables }] : [];
  if (mode === "page") {
    const byPage = new Map<number, ExtractedTable[]>();
    for (const t of tables) for (const p of t.pages) (byPage.get(p) ?? byPage.set(p, []).get(p)!).push(t);
    return Array.from(byPage.entries()).sort((a, b) => a[0] - b[0]).map(([p, ts]) => ({ name: `Page ${p}`, tables: ts }));
  }
  return tables.map((t, i) => ({ name: `Table ${i + 1} (${pageRangeLabel(t.pages)})`, tables: [t] }));
}

export interface PreviewSheet {
  name: string;
  tableCount: number;
  rowCount: number;
  columnCount: number;
  /** First rows as text, with whether the cell was written as a number/date (shown right-aligned). */
  rows: { text: string; numeric: boolean }[][];
  headerRows: number;
}

export interface WorkbookResult {
  workbook: XLSX.WorkBook;
  preview: PreviewSheet[];
  notes: string[];
  numericCells: number;
  dateCells: number;
  rowCount: number;
}

const PREVIEW_ROWS = 8;

function colLetters(n: number): string {
  let s = "";
  for (n++; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

/** Builds the workbook: real numbers and dates (when asked), widths that fit, a filter on single-table sheets. */
export function buildWorkbook(plans: SheetPlan[], options: ExcelOptions): WorkbookResult {
  const workbook = XLSX.utils.book_new();
  const preview: PreviewSheet[] = [];
  const notes: string[] = [];
  let numericCells = 0, dateCells = 0, rowCount = 0;
  /** Every number cell with its column's heading, so one column gets one format on every sheet. */
  const numberCells: { cell: { z?: string }; key: string; isDate: boolean }[] = [];

  for (const plan of plans) {
    const sheet: XLSX.WorkSheet = {};
    const widths: number[] = [];
    const pv: PreviewSheet["rows"] = [];
    let r = 0, maxCols = 0;
    let firstHeader = 0;
    plan.tables.forEach((table, ti) => {
      if (ti > 0) r++; // one blank row between stacked tables
      if (ti === 0) firstHeader = table.headerRows;
      const converted = options.convertValues
        ? convertTable(table.rows, table.headerRows, options.dateOrder)
        : { cells: table.rows.map((row) => row.map((v) => ({ t: "s", v }) as SheetCell)), notes: [] as string[], numericCells: 0, dateCells: 0 };
      for (const n of converted.notes) {
        const note = plan.tables.length > 1 || plans.length > 1 ? `${plan.name}: ${n}` : n;
        if (!notes.includes(note)) notes.push(note);
      }
      numericCells += converted.numericCells;
      dateCells += converted.dateCells;
      converted.cells.forEach((row, ri) => {
        row.forEach((cell, c) => {
          maxCols = Math.max(maxCols, c + 1);
          const text = table.rows[ri][c] ?? "";
          widths[c] = Math.max(widths[c] ?? 8, Math.min(60, text.length + 2));
          if (cell.t === "s") {
            if (cell.v !== "") sheet[colLetters(c) + (r + 1)] = { t: "s", v: cell.v };
          } else {
            const out = { t: "n" as const, v: cell.v, z: cell.z };
            sheet[colLetters(c) + (r + 1)] = out;
            numberCells.push({ cell: out, key: `${c}|${table.rows.slice(0, table.headerRows).map((h) => h[c] ?? "").join(" ").trim().toLowerCase()}|${cell.t}|${numberFormatKind(cell.z)}`, isDate: cell.t === "d" });
          }
        });
        if (pv.length < PREVIEW_ROWS) pv.push(row.map((cell, c) => ({ text: table.rows[ri][c] ?? "", numeric: cell.t !== "s" })));
        r++;
        rowCount++;
      });
    });
    if (r === 0) continue;
    sheet["!ref"] = `A1:${colLetters(Math.max(0, maxCols - 1))}${r}`;
    sheet["!cols"] = widths.map((w) => ({ wch: w }));
    if (plan.tables.length === 1 && firstHeader > 0 && r > 2) sheet["!autofilter"] = { ref: sheet["!ref"] };
    XLSX.utils.book_append_sheet(workbook, sheet, plan.name.slice(0, 31));
    preview.push({
      name: plan.name,
      tableCount: plan.tables.length,
      rowCount: r,
      columnCount: maxCols,
      rows: pv,
      headerRows: firstHeader,
    });
  }
  if (plans.length > 1) {
    const byKey = new Map<string, string[]>();
    for (const n of numberCells) if (!n.isDate && n.cell.z) (byKey.get(n.key) ?? byKey.set(n.key, []).get(n.key)!).push(n.cell.z);
    for (const n of numberCells) if (!n.isDate && n.cell.z) n.cell.z = mergeNumberFormats(byKey.get(n.key)!);
  }
  return { workbook, preview, notes, numericCells, dateCells, rowCount };
}

export function workbookToBlob(workbook: XLSX.WorkBook): Blob {
  const buffer = XLSX.write(workbook, { type: "array", bookType: "xlsx", cellDates: false });
  return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}
